import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  GameTypeEnum,
  BetTypeEnum,
  AvailableLanguagesEnum,
  TabEnum,
} from "../enums/index";

import { IAppConfig } from "../interfaces/index";
import { ApiConsumer } from "../services/ApiConsumer";

const initialState: IAppConfig = {
  numberStakeMultipliers: [],
  colorStakeMultipliers: [],
  parityStakeMultipliers: [],
  pulsatingSpinnerTimeout: 4000,
  countdownTimeout: 3000,
  balance: -1,
  currency: "",
  isBettingEnabled: true,
  isTicketTerminated: false,
  maxLocationStake: -1,
  winAmmount: -1,
  isConnectionLost: false,
  iosOrMacOsPlatform: false,
  gameType: -1,
  isDesktopScreen: false,
  languageIcon: AvailableLanguagesEnum.ENG,
  activeTab: TabEnum.BETTING,
  userInfo: {
    username: "",
    email: "",
  },
};

export const fetchConfig = createAsyncThunk("fetchConfig/config", async () => {
  const response = await ApiConsumer.getConfig();
  return response;
});

export const configSlice = createSlice({
  name: "config",
  initialState,
  reducers: {
    setWin: (state, { payload }) => {
      state.winAmmount = payload;
    },
    clearWin: (state) => {
      state.winAmmount = -1;
    },
    setCredit: (state, { payload }) => {
      state.balance = payload.balance;
      if (payload.currency) {
        state.currency = payload.currency;
      }
    },
    setIsBettingEnabled(state, { payload }) {
      state.isBettingEnabled = payload;
      state.isTicketTerminated = !payload;
    },
    setIsConnectionLost(state, { payload }) {
      state.isConnectionLost = payload;
    },
    setIosOrMacOsPlatform(state, { payload }) {
      state.iosOrMacOsPlatform = payload;
    },
    setLanguageIcon(state, { payload }) {
      state.languageIcon = payload;
    },
    setActiveTab(state, { payload }) {
      state.activeTab = payload;
    },
    setUserInfo: (state, { payload }) => {
      if (payload.email) {
        state.userInfo.email = payload.email;
      }
      if (payload.username) {
        state.userInfo.username = payload.username;
      }
    },
    setGameType(state, { payload }) {
      state.gameType = payload;

      if (payload !== GameTypeEnum.ONLINE) {
        state.isDesktopScreen = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchConfig.fulfilled, (state, { payload }) => {
      state.maxLocationStake = payload.maxStake;
      state.isBettingEnabled = payload.isBettingEnabled;
      state.isTicketTerminated = false;
      state.isConnectionLost = false;
      if (
        payload.gameConfig.some((game) => game.betTypeId === BetTypeEnum.NUMBER)
      ) {
        const game = payload.gameConfig.find(
          (gameItem) => gameItem.betTypeId === BetTypeEnum.NUMBER
        );
        if (game) {
          state.numberStakeMultipliers = [
            game.stakeFieldOne,
            game.stakeFieldTwo,
            game.stakeFieldThree,
            game.stakeFieldFour,
          ];
        }
      }
      if (
        payload.gameConfig.some((game) => game.betTypeId === BetTypeEnum.COLOR)
      ) {
        const game = payload.gameConfig.find(
          (gameItem) => gameItem.betTypeId === BetTypeEnum.COLOR
        );
        if (game) {
          state.colorStakeMultipliers = [
            game.stakeFieldOne,
            game.stakeFieldTwo,
            game.stakeFieldThree,
            game.stakeFieldFour,
          ];
        }
      }
      if (
        payload.gameConfig.some((game) => game.betTypeId === BetTypeEnum.PARITY)
      ) {
        const game = payload.gameConfig.find(
          (gameItem) => gameItem.betTypeId === BetTypeEnum.PARITY
        );
        if (game) {
          state.parityStakeMultipliers = [
            game.stakeFieldOne,
            game.stakeFieldTwo,
            game.stakeFieldThree,
            game.stakeFieldFour,
          ];
        }
      }
    });
  },
});

export const {
  setIsBettingEnabled,
  setCredit,
  setWin,
  clearWin,
  setIsConnectionLost,
  setIosOrMacOsPlatform,
  setGameType,
  setLanguageIcon,
  setActiveTab,
  setUserInfo,
} = configSlice.actions;

export default configSlice.reducer;
